import type { SessionRecordingSummary } from '@/types';
import {
  TIMELINE_EPSILON_SECONDS,
  type VideoSessionTimeline,
} from '@/utils/videoSessionTimeline';

export interface RecordingMediaChapter {
  videoId: string;
  mediaStart: number;
  mediaEnd: number;
  sessionStart: number;
  sessionEnd: number;
}

export interface RecordingMediaTimeline {
  recordingId: string;
  chapters: RecordingMediaChapter[];
  mediaDuration: number;
}

const isPositiveFiniteNumber = (
  value: number | null | undefined
): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

export const buildRecordingMediaTimeline = (
  recording: Pick<SessionRecordingSummary, 'id' | 'chapters'>,
  sessionTimeline: VideoSessionTimeline | null
): RecordingMediaTimeline | null => {
  if (!sessionTimeline) {
    return null;
  }

  const sessionClipsByVideoId = new Map(
    sessionTimeline.clips.map((clip) => [clip.video.id, clip])
  );
  const chapters: RecordingMediaChapter[] = [];
  let mediaOffset = 0;

  for (const chapter of recording.chapters) {
    const duration = chapter.duration;
    if (!isPositiveFiniteNumber(duration)) {
      continue;
    }

    const sessionClip = sessionClipsByVideoId.get(chapter.id);
    if (sessionClip) {
      const mappedDuration = Math.min(duration, sessionClip.duration);
      chapters.push({
        videoId: chapter.id,
        mediaStart: mediaOffset,
        mediaEnd: mediaOffset + mappedDuration,
        sessionStart: sessionClip.startTime,
        sessionEnd: sessionClip.startTime + mappedDuration,
      });
    }
    mediaOffset += duration;
  }

  return chapters.length > 0
    ? {
        recordingId: recording.id,
        chapters,
        mediaDuration: mediaOffset,
      }
    : null;
};

export const getRecordingMediaTime = (
  timeline: RecordingMediaTimeline | null,
  sessionTime: number,
  preferredVideoId?: string | null
): number | null => {
  if (!timeline || !Number.isFinite(sessionTime)) {
    return null;
  }

  const matchingChapters = timeline.chapters.filter(
    (chapter) =>
      sessionTime >= chapter.sessionStart - TIMELINE_EPSILON_SECONDS &&
      sessionTime <= chapter.sessionEnd + TIMELINE_EPSILON_SECONDS
  );
  if (matchingChapters.length === 0) {
    return null;
  }

  const preferredChapter = preferredVideoId
    ? matchingChapters.find((chapter) => chapter.videoId === preferredVideoId)
    : null;
  const chapter =
    preferredChapter && sessionTime < preferredChapter.sessionEnd
      ? preferredChapter
      : matchingChapters.reduce((latest, candidate) =>
          candidate.sessionStart >= latest.sessionStart ? candidate : latest
        );

  return (
    chapter.mediaStart +
    Math.min(
      Math.max(sessionTime - chapter.sessionStart, 0),
      chapter.mediaEnd - chapter.mediaStart
    )
  );
};

export const getRecordingSessionTime = (
  timeline: RecordingMediaTimeline | null,
  mediaTime: number
): number | null => {
  if (!timeline || !Number.isFinite(mediaTime)) {
    return null;
  }

  const chapter = timeline.chapters
    .filter(
      (candidate) =>
        mediaTime >= candidate.mediaStart - TIMELINE_EPSILON_SECONDS &&
        mediaTime <= candidate.mediaEnd + TIMELINE_EPSILON_SECONDS
    )
    .reduce<RecordingMediaChapter | null>(
      (latest, candidate) =>
        !latest || candidate.mediaStart >= latest.mediaStart
          ? candidate
          : latest,
      null
    );
  if (!chapter) {
    return null;
  }

  return (
    chapter.sessionStart +
    Math.min(
      Math.max(mediaTime - chapter.mediaStart, 0),
      chapter.sessionEnd - chapter.sessionStart
    )
  );
};
